import { ICheckoutProps } from './types';
import { usePayableAmount } from 'context/hooks/usePayableAmount';
import { findFeeQuote, methodForPage } from 'utils/feeQuote';
import { formatCurrency } from 'utils';
import Icon from 'components/ui/Icon/Icon';
import { usePageContext } from 'context/hooks/usePageContext';
import PaymentSelector from 'components/ui/Button/PaymentSelector';
import PageSwitcher from 'components/PageSwitcher';
import Authenticating from 'components/screens/Authenticating';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { PaymentHandlerFactory } from 'services/paymentHandlers';
import { getErrorMessage } from 'services/paymentHandlers/apiError';
import { isCustomsCheckout, resolveProvider } from 'config/providers';
import { formatAssessmentReference, parseCustomsMetadata } from 'utils/customs';
import { buildTransactionMetadata } from 'utils/metadata';
import { ReactComponent as Transfer } from './assets/icons/transfer.svg';
import { ReactComponent as CreditCard } from './assets/icons/creditcard.svg';
import { ReactComponent as Opay } from './assets/icons/opay.svg';
import { ReactComponent as Cancel } from './assets/icons/cancel.svg';
import { useTransferContext } from 'context/hooks/useTransferContext';
import { useErrorContext } from 'context/hooks/useErrorContext';

import { ReactComponent as Spinner } from './assets/icons/spinner.svg';
import { useEffect, useMemo, useRef } from 'react';
import { PAGE_SUB, PAGES } from 'context/pageContext';
import { PAYMENT_PROVIDER } from 'types/enums';
import { useTimerContext } from 'context/hooks/useTimerContext';
import useRecordCardAttempt from 'hooks/useRecordCardAttempt';

function App({
  customerEmail,
  customerPhone,
  customerId,
  amount,
  invoiceId,
  currency,
  customerRef,
  publicKey,
  assessmentNoticeEntryType,
  sodYear,
  customsCode,
  sodAssessmentSerial,
  sodAssessmentNumber,
  payersName,
  metadata,
}: ICheckoutProps) {
  useRecordCardAttempt();
  const transactionInitiatedRef = useRef(false);
  const { changePage, changeSub, page } = usePageContext();
  const {
    ceviantTransactionId,
    merchantName,
    initiateTransaction,
    changeTransactionDetails,
    selectProvider,
    provider,
    feeQuotes,
  } = useTransactionContext();
  const { changeTransferDetails, virtualAccountNumber, validityTime } =
    useTransferContext();
  const { startTimer } = useTimerContext();
  const { clearError } = useErrorContext();
  // what the customer pays for the method they are on: base amount, our quoted fee and the provider's fee
  const payable = usePayableAmount(methodForPage(page));
  const displayAmount = payable.displayTotal / 100;

  // remember the method so the success screen can show what was paid
  useEffect(() => {
    const method = methodForPage(page);
    if (method) {
      changeTransactionDetails({ paymentMethod: method });
    }
  }, [page, changeTransactionDetails]);

  // Read from the props rather than the resolved provider, so the customs
  // layout is in place from the first render instead of after allProviders.
  const customs = useMemo(
    () =>
      parseCustomsMetadata({
        assessmentNoticeEntryType,
        sodYear,
        customsCode,
        sodAssessmentSerial,
        sodAssessmentNumber,
        payersName,
      }),
    [
      assessmentNoticeEntryType,
      sodYear,
      customsCode,
      sodAssessmentSerial,
      sodAssessmentNumber,
      payersName,
    ],
  );
  const isCustomsMode = isCustomsCheckout(
    customs.status !== 'absent',
    provider,
  );

  const handlePageChange = (name: unknown) => {
    if (name === page) return;

    clearError();
    changePage(name as PAGES);
    changeSub(name as PAGE_SUB);
  };

  const handleInitiateTransfer = () => {
    if (page !== PAGES.TRANSFER) {
      clearError();
    }

    const transferProvider = resolveProvider(provider as PAYMENT_PROVIDER);
    selectProvider(transferProvider);
    initiateTransfer(transferProvider);
  };

  const initiateTransfer = async (transferProvider: PAYMENT_PROVIDER) => {
    changePage(PAGES.TRANSFER);
    if (virtualAccountNumber === '') {
      changeSub(PAGE_SUB.LOADING);
      const customsMetadata =
        customs.status === 'valid' ? customs.metadata : undefined;

      try {
        const details = await PaymentHandlerFactory.transfer(
          transferProvider,
        ).initiateTransfer({
          transactionId: ceviantTransactionId,
          // the quote for the rail this transfer goes through (our virtual account, or FirstChekout's own)
          amount:
            findFeeQuote(feeQuotes, 'TRANSFER', transferProvider)
              ?.totalAmount ?? amount,
          email: customerEmail,
          payerName: customsMetadata?.payersName,
          purpose:
            customsMetadata && formatAssessmentReference(customsMetadata),
        });

        if (details) {
          changeTransferDetails(details);
          changeSub(PAGE_SUB.TRANSFER);
        }
      } catch (error) {
        changeSub(PAGE_SUB.ERROR, getErrorMessage(error));
      }
    } else {
      changeSub(PAGE_SUB.TRANSFER);
    }
  };

  useEffect(() => {
    if (!transactionInitiatedRef.current && ceviantTransactionId === '') {
      changeTransactionDetails({
        customerEmail,
        customerPhone,
        amount,
        invoiceId,
        currency,
        customerId,
        customerRef,
        publicKey,
      });
      transactionInitiatedRef.current = true;

      if (customs.status === 'partial') {
        changeSub(
          PAGE_SUB.ERROR,
          `Missing customs assessment details: ${customs.missing.join(', ')}`,
        );
        return;
      }

      initiateTransaction({
        customerEmail,
        customerPhone,
        amount,
        invoiceId,
        currency,
        customerId,
        customerRef,
        metadata: buildTransactionMetadata(metadata, customs),
        isCustomsPayment: customs.status === 'valid',
      });
    }
  }, [
    amount,
    ceviantTransactionId,
    changeSub,
    changeTransactionDetails,
    currency,
    customerEmail,
    customerId,
    customerPhone,
    customs,
    metadata,
    initiateTransaction,
    invoiceId,
    customerRef,
    publicKey,
  ]);

  useEffect(() => {
    if (validityTime && validityTime > 0) {
      startTimer(validityTime * 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validityTime]);

  return (
    <>
      {page === PAGES.AUTHENTICATING && (
        <Authenticating amount={displayAmount} />
      )}
      {page !== PAGES.AUTHENTICATING && (
        <div
          className="font-primary flex w-screen h-screen max-h-screen 
      lg:bg-Primary-200/80 bg-white justify-center overflow-hidden"
        >
          <div className="w-full lg:grid lg:place-items-center lg:p-10">
            {/* Mobile */}
            <div className="bg-[#f4f4f4] h-screen lg:hidden w-full">
              {page === PAGES.LOADING && (
                <div>
                  <img
                    src="/ceviant_logo.svg"
                    alt="logo"
                    className="p-4 pb-0"
                  />
                  <h3 className="text-center py-6 text-xl">
                    You are about to pay{' '}
                    <span className="font-bold">
                      {formatCurrency(displayAmount, 'NGN')}
                    </span>{' '}
                    to {merchantName}
                  </h3>
                  <div className="border-b border-t">
                    <div
                      className="flex items-center gap-2 text-[#4c4c4c] p-4 cursor-pointer"
                      onClick={() => handlePageChange(PAGES.CARD)}
                    >
                      <Icon size={16} svg={CreditCard} />
                      Pay with Card
                    </div>
                    <div
                      className="flex items-center gap-2 text-[#4c4c4c] p-4 cursor-pointer border-b"
                      onClick={handleInitiateTransfer}
                    >
                      <Icon size={16} svg={Transfer} />
                      <p>Pay with Transfer</p>
                    </div>
                    {!isCustomsMode && (
                      <div
                        className="flex items-center gap-2 text-[#4c4c4c] p-4 cursor-pointer"
                        onClick={() => handlePageChange(PAGES.OPAY)}
                      >
                        <Icon width={54} height={20} svg={Opay} />
                        Pay with Opay
                      </div>
                    )}
                  </div>
                  <button
                    className="flex items-center gap-1 border p-2 m-auto mt-8 text-[#2E3C4E] bg-[#e4e4e4] text-sm 
                  font-semibold rounded-md"
                    type="button"
                    onClick={() =>
                      window.parent.postMessage(
                        { message: 'closeCeviantSDK' },
                        '*',
                      )
                    }
                  >
                    <Icon size={20} svg={Cancel} className="text-[#2E3C4E]" />
                    Cancel Payment
                  </button>
                </div>
              )}
              {page !== PAGES.LOADING && (
                <PageSwitcher
                  amount={amount}
                  email={customerEmail}
                  isCustomsMode={isCustomsMode}
                />
              )}
            </div>

            {/* Desktop */}
            <div className="max-lg:hidden bg-[#f4f4f4] mx-auto w-full max-w-5xl max-h-[620px] rounded-2xl h-full flex">
              <div className="bg-white w-1/3 p-5 pt-10 space-y-8 h-full rounded-es-2xl rounded-ss-2xl">
                <header>
                  <img
                    src="/ceviant_logo.svg"
                    alt="logo"
                    className="w-[80%] m-auto"
                  />
                </header>
                <div className="pl-4">
                  <p className="uppercase text-[#121212] mb-4 font-semibold text-xl pl-2">
                    Pay With
                  </p>
                  <div className="space-y-2">
                    <PaymentSelector
                      name={PAGES.CARD}
                      icon={CreditCard}
                      handlePageChange={handlePageChange}
                      disabled={!ceviantTransactionId}
                    />
                    <PaymentSelector
                      name={PAGES.TRANSFER}
                      icon={Transfer}
                      onClick={handleInitiateTransfer}
                      disabled={!ceviantTransactionId}
                    />
                    <PaymentSelector
                      name={PAGES.OPAY}
                      icon={Opay}
                      handlePageChange={handlePageChange}
                      disabled={!ceviantTransactionId}
                      isOpay
                    />
                  </div>
                </div>
              </div>
              <div
                className={`px-10 py-6 w-2/3 h-full max-h-[800px] space-y-6 overflow-y-auto`}
              >
                {page === PAGES.LOADING && ceviantTransactionId === '' ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Icon svg={Spinner} size={70} />
                    <p>Loading</p>
                  </div>
                ) : (
                  <section
                    className={`flex flex-col items-stretch justify-center min-h-full gap-4`}
                  >
                    <h3 className="text-center text-base">
                      You are about to pay{' '}
                      <span className="font-semibold">
                        {formatCurrency(displayAmount, 'NGN')}
                      </span>{' '}
                      to {merchantName}
                    </h3>
                    <PageSwitcher
                      amount={amount}
                      email={customerEmail}
                      isCustomsMode={isCustomsMode}
                    />
                    {/* <div className="flex items-center gap-3 justify-center text-[#1A1C21] text-xl">
                      Powered by <img src="/ceviant_logo.svg" alt="logo" />
                    </div> */}
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
